/**
 * The student's own plan, and the plans they can buy.
 *
 * Ported from the app's `screens/plans_screen.dart` and
 * `widgets/subscription_card.dart`.
 *
 * *** NOT THE ADMIN PLANS SCREEN ***
 * `/subscription-plans` is where staff define what the tenant sells. This is
 * a student asking what they hold and what they may buy, from
 * `/me/subscription` and `/me/plans`.
 *
 * *** EVERY RULE IS THE SERVER'S ***
 * Whether this tenant uses subscriptions at all, whether buying is allowed,
 * why a plan is not usable, and what to say about a payment in flight all
 * arrive computed. Deciding any of them here would mean a build disagreeing
 * with the server it is talking to.
 */

import { useState } from 'react'
import { PageHeader, PageStack, Card } from '@/components/ui/Page'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useMyPlans, useMySubscription, usePurchasePlan } from '@/lib/queries'
import { useToast } from '@/components/ui/Toast'
import type { PublicPlan, PurchaseStarted } from '@/types/api'
import { CurrentPlanCard, PlanCard, SubscriptionCard } from './SubscriptionCard'
import { PaymentWaiting } from './PaymentWaiting'
import { SubscriptionHistoryList } from './SubscriptionHistoryList'
import styles from './PlansPage.module.css'

export function MyPlanPage() {
  const { user } = useAuth()
  const toast = useToast()

  const sub = useMySubscription()
  const plans = useMyPlans()
  const purchase = usePurchasePlan()

  /* Set once a purchase has started; the waiting screen owns the page then. */
  const [started, setStarted] = useState<PurchaseStarted | null>(null)
  const [buying, setBuying] = useState<string | null>(null)

  const buy = async (plan: PublicPlan) => {
    setBuying(plan.plan_id)
    try {
      const result = await purchase.mutateAsync(plan.plan_id)

      /*
        *** A REDIRECT GATEWAY HAS NO IN-APP SHEET ***
        ICICI's Initiate Sale returns a one-shot URL and the customer must
        land on the bank's own hosted page. `assign`, not `open`: a popup is
        blocked by default and the student would be left on an unchanged
        page having tapped Buy.

        The server's return URL brings them back to `/payment-result`, and
        the waiting screen below picks it up from there.
      */
      if (result.checkout_mode === 'redirect' && result.checkout_url) {
        /*
          *** TELL THE RESULT PAGE A BROWSER STARTED THIS ***
          The bank returns to `/payment-result`, a static page in
          `Mentor_SITE` shared with the mobile app — so it told everyone to
          "open the MentorsConnect app", which a web student may not have.
          This marker is how that page knows to offer a link back here
          instead. Same origin, same browser, so it survives the round trip.

          Wrapped: sessionStorage throws in a private window with site data
          blocked, and the payment must still go ahead — the student just
          gets the app wording, which is only unhelpful, not wrong.
        */
        try {
          sessionStorage.setItem('mentor.webCheckout', '1')
        } catch {
          /* Not worth blocking a payment over. */
        }
        window.location.assign(result.checkout_url)
        return
      }

      /*
        CASH, or a gateway that returned no URL. Either way there is nothing
        to redirect to and the server's `next_step` says what happens now.
      */
      setStarted(result)
    } catch (caught) {
      /*
        The server names which rule refused: a plan already held (409), the
        bank not responding (502), a plan that was withdrawn. Each needs a
        different action from the student, and "could not buy" names none.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not start this purchase.',
      )
    } finally {
      setBuying(null)
    }
  }

  /*
    *** AN EMPTY LIST IS A CLAIM ***
    React Query serves a cached list instantly on a revisit, so `isLoading`
    alone would announce "no plans" from data that has not answered.
  */
  const plansSettled = plans.isSuccess && !plans.isFetching
  const list = plans.data?.plans ?? []

  if (started) {
    return (
      <PageStack>
        <PageHeader title="Your plan" />
        <PaymentWaiting started={started} onClose={() => setStarted(null)} />
      </PageStack>
    )
  }


  return (
    <PageStack>
      <PageHeader
        title="Your plan"
        description="Sessions you have, and plans you can buy."
      />

      {sub.isLoading && <p className={styles.muted}>Loading your plan…</p>}

      {sub.error && (
        <p className={styles.error}>
          {sub.error instanceof ApiError
            ? sub.error.message
            : 'Could not load your plan.'}
        </p>
      )}

      {/*
        *** A PER-SESSION CLIENT HAS NO PLANS AT ALL ***
        `subscription_billing` is false and this whole surface is wrong for
        them — they pay per booking. Said plainly rather than showing an
        empty plan list, which reads as "your institute sells nothing".
      */}
      {sub.data && !sub.data.subscription_billing && (
        <Card>
          <p className={styles.muted}>
            {user?.tenant_name ?? 'Your institute'} charges for each session
            rather than selling plans. You pay when you book.
          </p>
        </Card>
      )}

      {sub.data?.subscription_billing && (
        <>
          {/*
            *** THE PLAN THEY HOLD LEADS ***
            Present only when it is a DIFFERENT subscription from the one the
            card below describes — the server decides that, so the two can
            never show the same plan twice.

            First, because "what do I have" outranks "what went wrong with my
            last attempt". The attempt card still follows, because a student
            whose purchase failed needs to know that too.
          */}
          {sub.data.current_plan && (
            <CurrentPlanCard plan={sub.data.current_plan} />
          )}

          <SubscriptionCard sub={sub.data} onBuy={null} />

          {/*
            *** THE SERVER DECIDES WHETHER BUYING IS OFFERED ***
            `can_purchase` is true only when nothing usable is held. Listing
            plans against anything else offers buttons the server refuses,
            and on a dormant purchase it would take a second payment for a
            plan already bought.
          */}
          {sub.data.can_purchase ? (
            <>
              {plans.isLoading && (
                <p className={styles.muted}>Loading plans…</p>
              )}

              {plansSettled && list.length === 0 && (
                <Card>
                  <p className={styles.muted}>
                    No plans are on sale right now. Ask your institute.
                  </p>
                </Card>
              )}

              {list.length > 0 && (
                <section>
                  {/*
                    *** "BUY ANOTHER PLAN" READS AS AN UPSELL ***
                    `can_purchase` is true ONLY when nothing usable is held,
                    so every student seeing this list is blocked from
                    booking — they are not topping up, they are getting
                    going again. "Another" suggested the optional kind of
                    purchase and buried the point.
                  */}
                  <h2 className={styles.sectionTitle}>
                    {sub.data.plan_name
                      ? 'Get more sessions'
                      : 'Choose a plan'}
                  </h2>
                  <div className={styles.planGrid}>
                    {list.map((plan) => (
                      <PlanCard
                        key={plan.plan_id}
                        plan={plan}
                        disabled={buying !== null}
                        busy={buying === plan.plan_id}
                        onBuy={() => buy(plan)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : null}

          {/*
            *** WHAT THEY HAVE ACTUALLY BOUGHT ***
            Everything above reports ONE subscription — the newest, or the
            one still running. A student who has bought four plans could
            see no trace of the first three (raised 2026-10-31).
          */}
          <SubscriptionHistoryList />
        </>
      )}
    </PageStack>
  )
}
